import { useEventListener, watchImmediate } from "@vueuse/core";
import { computed, ref, toValue } from "vue";

import { useHostElement } from "#ce/composables/useHostElement";
import { useListRole } from "#ce/components/SpList/composables/useListRole";
import { toBoolean } from "#ce/utils/props";
import { isObjectLike } from "#ce/utils/types";

import { useActivatorPopupState } from "./useActivatorPopupState.js";

/**
 * Publishes the picker's popup state onto its activator, and returns the role that popup exposes.
 *
 * Two sources answer for the popup. The *prediction* is what the root rows say will open. The
 * *report* is what the panel this picker renders states it holds: `null` until it has reported,
 * `undefined` for a list stating no role, or the role. The report counts as evidence only while
 * it describes the panel the next open shows, and `popupRole` resolves the two in this order:
 *
 *   1. readonly                        → no role, whatever either source says
 *   2. report is not evidence          → prediction
 *   3. report `null`                   → prediction
 *   4. report no role, loading         → prediction
 *   5. report no role, not loading     → no role
 *   6. report names a role             → that role
 *
 * The prediction is `listbox` for no rows while loading, no role for no rows otherwise, and the
 * role the root rows form.
 *
 * @param {object} source
 * @param {import('vue').MaybeRefOrGetter<Array|undefined>} source.items - the picker's rows.
 * @param {import('vue').MaybeRefOrGetter<boolean|string|undefined>} source.readonly - whether the
 *   list only navigates.
 * @param {import('vue').MaybeRefOrGetter<boolean|string|undefined>} source.loading - whether rows
 *   are still being fetched, which is what tells an empty picker apart from one that opens onto
 *   nothing.
 * @param {import('vue').MaybeRefOrGetter<boolean|string|undefined>} source.disabled - whether the
 *   picker is unavailable. A report the panel makes while it is disabled is discarded.
 * @param {import('vue').MaybeRefOrGetter<boolean|string|undefined>} source.startOnRoot - whether
 *   opening remounts the root, which decides whether a closed picker reopens on what it last showed.
 * @param {import('vue').MaybeRefOrGetter<string|undefined>} source.navigation - the container the
 *   panel renders with, which decides how a branch row reaches the list.
 * @param {import('vue').MaybeRefOrGetter<boolean>} source.open - whether the panel is open.
 * @param {import('vue').Ref<HTMLSlotElement | null>} source.slot - the activator slot.
 * @param {import('vue').MaybeRefOrGetter<boolean>} source.enabled - false where there is no popup
 *   to describe, which releases every activator.
 * @returns {{ popupRole: import('vue').ComputedRef<string|undefined> }}
 */
export function useQuantumSelectPopupState({
  items,
  readonly,
  loading,
  disabled,
  startOnRoot,
  navigation,
  open,
  slot,
  enabled,
}) {
  const rows = () => toValue(items) ?? [];
  const isLoading = () => toBoolean(toValue(loading));

  // `quantumSelectProps` drops `itemProps`, so the per-item key is the list's own default and the
  // readers are supplied here.
  const { listRole: rowsRole } = useListRole({
    items: rows,
    readonly,
    tree: () => toValue(navigation) === "accordion" && rows().some((item) => item?.hasChildren),
    // `SpList` reads a header as the presence of the property, so a blank label is a header there
    // as well, and the two answers have to agree.
    isHeader: (item) => isObjectLike(item) && "header" in item,
    // A branch row reaches the list as either `selectable: false` or an interactive caret, and a
    // listbox may own neither — except under `flat`, which strips `hasChildren` and renders the row
    // as the plain option it then is.
    getItemProps: (item) =>
      item?.hasChildren && toValue(navigation) !== "flat" ? { ...item.props, selectable: false } : (item?.props ?? {}),
  });

  // No rows is two different states: still loading names the list that is coming, while a picker
  // that has none and is not fetching any opens onto nothing.
  const prediction = computed(() => {
    if (rows().length === 0) {
      return isLoading() ? "listbox" : undefined;
    }

    return rowsRole.value;
  });

  const report = ref(null);
  const host = useHostElement();

  // Only the panel this picker renders: a consumer may slot a list of its own into the activator,
  // whose events reach this root through the slot it is assigned to. And only while enabled: a
  // disabled picker makes its list readonly, so the list then states no role for a reason that is
  // the picker's own. The panel reports again once the picker is enabled.
  function handleListRole(event) {
    if (event.target?.getRootNode() !== host?.shadowRoot || toBoolean(toValue(disabled))) {
      return;
    }

    const [reported] = event.detail ?? [];

    report.value = reported === null ? null : (reported ?? undefined);
  }

  // The shadow root rather than a template ref: a picker rendered open has a list from the first
  // frame, and that list reports while it sets up, before a template ref resolves.
  function listRoot() {
    return host?.shadowRoot;
  }

  const shown = ref(false);

  function markShown(open) {
    shown.value = shown.value || toBoolean(open);
  }

  useEventListener(listRoot, "update-role", handleListRole);
  watchImmediate(() => toValue(open), markShown);

  // Whether the report describes the panel the next open shows. Before the first open it describes
  // a panel nobody has seen. Afterwards the panel is retained across a close, so it does — except
  // under `start-on-root`, where opening remounts the root, so a closed picker opens on its root.
  const reportIsEvidence = computed(() => shown.value && (toValue(open) || !toBoolean(toValue(startOnRoot))));

  const popupRole = computed(() => {
    if (toBoolean(toValue(readonly))) {
      return undefined;
    }

    if (!reportIsEvidence.value || report.value === null) {
      return prediction.value;
    }

    if (report.value === undefined) {
      return isLoading() ? prediction.value : undefined;
    }

    return report.value;
  });

  useActivatorPopupState({ slot, expanded: open, popupRole, enabled });

  return { popupRole };
}
